import React from 'react';
import {
  applyQuestionChoices,
  getQuestionChoices,
  isTextQuestion,
  parseChoicesInput,
  summarizeQuestion,
} from '../lib/questionText';
import BulkPasteModal from './BulkPasteModal';
import ChoiceCard from './ChoiceCard';
import GapCard from './GapCard';
import { addButton, button, hint, input, label as labelStyle } from '../../../shared/ui/formStyles';

const LocalizedRow = ({ title, description, value, onChange, rows }) => (
  <div style={{ marginBottom: '18px' }}>
    <span style={labelStyle}>{title}</span>
    {description && <p style={{ ...hint, margin: '0 0 8px' }}>{description}</p>}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
      {['ua', 'en'].map((locale) => (
        <div key={locale}>
          <span style={{ fontSize: '11px', color: '#94A3B8' }}>
            {locale === 'ua' ? 'Українською' : 'Англійською'}
          </span>
          {rows ? (
            <textarea
              rows={rows}
              value={value?.[locale] || ''}
              onChange={(e) => onChange({ ...value, [locale]: e.target.value })}
              style={{ ...input, marginTop: '3px' }}
            />
          ) : (
            <input
              type="text"
              value={value?.[locale] || ''}
              onChange={(e) => onChange({ ...value, [locale]: e.target.value })}
              style={{ ...input, marginTop: '3px' }}
            />
          )}
        </div>
      ))}
    </div>
  </div>
);

const QuestionEditor = ({ question, index, onChange }) => {
  const textQuestion = isTextQuestion(question);
  const summary = summarizeQuestion(question);
  const choices = getQuestionChoices(question);
  const groups = question.groups || [];
  const questionWeight = groups[0]?.weight ?? 1;

  const [isPasteOpen, setPasteOpen] = React.useState(false);

  const patch = (changes) => onChange({ ...question, ...changes });

  const nextGroupId = (offset = 0) => {
    const used = new Set(groups.map((group) => group.id));
    let number = groups.length + 1 + offset;

    while (used.has(`${question.id}-g${number}`)) {
      number += 1;
    }

    return `${question.id}-g${number}`;
  };

  const setGroup = (groupIndex, nextGroup) =>
    patch({ groups: groups.map((group, i) => (i === groupIndex ? nextGroup : group)) });

  const removeGroup = (groupIndex) => {
    if (!window.confirm(`Видалити ${textQuestion ? 'речення' : 'питання'} ${groupIndex + 1}?`)) {
      return;
    }
    patch({ groups: groups.filter((_, i) => i !== groupIndex) });
  };

  const copyGroup = (groupIndex) => {
    const id = nextGroupId();
    const source = JSON.parse(JSON.stringify(groups[groupIndex]));
    const copy = {
      ...source,
      id,
      options: source.options?.map((option, optionIndex) => ({
        ...option,
        id: `${id}-o${optionIndex + 1}`,
      })),
    };
    const next = [...groups];

    next.splice(groupIndex + 1, 0, copy);
    patch({ groups: next });
  };

  const addGroup = () => {
    const id = nextGroupId();
    const previous = groups[groups.length - 1];

    patch({
      groups: [
        ...groups,
        textQuestion
          ? {
              id,
              label: { ua: '', en: '' },
              inputKind: previous?.inputKind || 'short',
              weight: previous?.weight ?? 1,
              acceptedAnswers: [''],
              ...(choices.length > 0 ? { choices: [...choices] } : {}),
            }
          : {
              id,
              label: { ua: '', en: '' },
              weight: previous?.weight ?? 1,
              options: ['A', 'B'].map((optionLabel, optionIndex) => ({
                id: `${id}-o${optionIndex + 1}`,
                label: optionLabel,
                text: { ua: '', en: '' },
                isCorrect: optionIndex === 0,
              })),
            },
      ],
    });
  };

  return (
    <div>
      <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', background: '#fff', padding: '18px', marginBottom: '16px' }}>
        <LocalizedRow
          title="Заголовок завдання"
          description="Показується зверху, над усіма реченнями. Наприклад: «Put a preposition into each gap»."
          value={question.prompt}
          onChange={(prompt) => patch({ prompt })}
        />

        <LocalizedRow
          title="Пояснення або текст для читання (необовʼязково)"
          description="Сюди вставляють інструкцію чи цілий уривок, якщо завдання на читання."
          value={question.helperText || { ua: '', en: '' }}
          rows={(question.helperText?.ua || question.helperText?.en || '').length > 300 ? 10 : 3}
          onChange={(helperText) =>
            patch({ helperText: helperText.ua || helperText.en ? helperText : null })
          }
        />

        {textQuestion && (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px' }}>
            <div>
              <span style={labelStyle}>Список слів на вибір (необовʼязково)</span>
              <input
                type="text"
                value={choices.join(', ')}
                placeholder="at, in, about, after, for"
                onChange={(e) =>
                  patch({ groups: applyQuestionChoices(groups, parseChoicesInput(e.target.value)) })
                }
                style={input}
              />
              <p style={hint}>
                Заповніть через кому — студент обиратиме зі списку. Порожньо — вписуватиме сам.
              </p>
            </div>
            <div>
              <span style={labelStyle}>Що вписує студент</span>
              <select
                value={groups[0]?.inputKind || 'short'}
                onChange={(e) =>
                  patch({ groups: groups.map((group) => ({ ...group, inputKind: e.target.value })) })
                }
                style={input}
              >
                <option value="short">Слово</option>
                <option value="sentence">Ціле речення</option>
              </select>
              <p style={hint}>Розмір поля для відповіді.</p>
            </div>
            <div>
              <span style={labelStyle}>Балів за відповідь</span>
              <input
                type="number"
                step="0.5"
                min="0"
                value={questionWeight}
                onChange={(e) => {
                  const weight = parseFloat(e.target.value);
                  patch({
                    groups: groups.map((group) => ({
                      ...group,
                      weight: Number.isFinite(weight) ? weight : 1,
                    })),
                  });
                }}
                style={input}
              />
              <p style={hint}>Разом за завдання: {summary.score} б.</p>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>
          {textQuestion ? 'Речення з пропусками' : 'Питання з варіантами'}: {summary.count}
        </span>
        <button type="button" onClick={() => setPasteOpen(true)} style={{ ...button, marginLeft: 'auto' }}>
          Вставити списком з Word
        </button>
      </div>

      {groups.map((group, groupIndex) =>
        textQuestion ? (
          <GapCard
            key={group.id || groupIndex}
            group={group}
            index={groupIndex}
            questionWeight={questionWeight}
            onChange={(nextGroup) => setGroup(groupIndex, nextGroup)}
            onCopy={() => copyGroup(groupIndex)}
            onRemove={() => removeGroup(groupIndex)}
          />
        ) : (
          <ChoiceCard
            key={group.id || groupIndex}
            group={group}
            index={groupIndex}
            onChange={(nextGroup) => setGroup(groupIndex, nextGroup)}
            onCopy={() => copyGroup(groupIndex)}
            onRemove={() => removeGroup(groupIndex)}
          />
        )
      )}

      <button type="button" onClick={addGroup} style={{ ...addButton, width: '100%' }}>
        + Додати {textQuestion ? 'речення' : 'питання'}
      </button>

      <p style={{ ...hint, marginTop: '14px' }}>
        Технічний номер завдання: {question.id} · у тесті йде {index + 1}-м
      </p>

      <BulkPasteModal
        isOpen={isPasteOpen}
        isTextQuestion={textQuestion}
        questionId={question.id}
        existingCount={groups.length}
        onCancel={() => setPasteOpen(false)}
        onConfirm={(newGroups) => {
          patch({
            groups: [
              ...groups,
              ...(textQuestion ? applyQuestionChoices(newGroups, choices) : newGroups),
            ],
          });
          setPasteOpen(false);
        }}
      />
    </div>
  );
};

export default QuestionEditor;
